import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenusStores from './menus-stores/menus-stores.component';
import { DateTime } from 'luxon';
import { useHasPermission } from 'hooks/authorization.hooks';
import {
  DeleteOutlined,
  EditOutlined,
  UploadOutlined,
} from '@mui/icons-material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useCatalogsBulkUpdateMutation,
  usePublishMenuMutation,
} from 'api/ordering-service/ordering-service';
import { ToolbarProps } from 'components/inc-data-grid/inc-data-grid.component';
import { additionalPropertiesToObject } from 'api/ordering-service/ordering-service.utils';
import { UtilityDrawerHookReturnType } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import {
  MenuResult,
  PublishMenuPayload,
  PublishMenuReturnType,
} from 'api/ordering-service/ordering-service.types';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import useMenusToast from '../../menus-toast/menus-toast.hooks';
import { useBulkEditDataGridContext } from 'components/inc-data-grid/bulk-edit-data-grid/bulk-edit-data-grid.context';
import {
  displayInfoColumnVisibilityModel,
  useMenuManagementBulkEditFields,
  useMenuManagementDisplayInfoColumns,
} from 'pages/menu-management/menu-management.hooks';
import {
  toBulkUpdateField,
  toBulkUpdateValue,
} from 'pages/menu-management/menu-management-data-grid.utils';
import IncActiveInactiveStatusChip from 'components/inc-data-grid/cells/inc-active-inactive-status-chip/inc-active-inactive-status-chip.component';
import { BulkEditGridColDef } from 'components/inc-data-grid/bulk-edit-data-grid.types';

/**
 * Re-polls the publish-menu request every 5s while it is IN_PROGRESS, and
 * stops once it reports COMPLETED.
 *
 * Extracted from useMenusDataGrid purely so it can be driven directly with
 * fake timers. Inline, its COMPLETED branch was only ever reached when some
 * unrelated Cypress spec happened to publish a menu AND stay on the page long
 * enough for a 5s tick, so it drifted in and out of the merged coverage report
 * -- one partial branch is enough to fail codecov's 100%/0%-threshold project
 * gate. See menus-data-grid.hooks.test.tsx.
 */
export const usePollPublishMenuStatus = (
  publishMenuData: PublishMenuReturnType | undefined,
  publishMenu: (payload: PublishMenuPayload) => void,
  storeIds: string[] | undefined,
) => {
  useEffect(() => {
    const timeoutId = setInterval(() => {
      if (publishMenuData?.status === 'IN_PROGRESS') {
        publishMenu({
          storeIds,
          requestId: publishMenuData?.requestId,
        });
      } else if (publishMenuData?.status === 'COMPLETED') {
        clearInterval(timeoutId);
      }
    }, 5000);

    return () => clearInterval(timeoutId);
  }, [
    storeIds,
    publishMenu,
    publishMenuData?.requestId,
    publishMenuData?.status,
  ]);
};

export const useMenusDataGrid = (
  isNative: boolean,
  deleteConfirmationDrawer: UtilityDrawerHookReturnType<MenuResult>,
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<MenuResult>();

  const [publishMenu, { data: publishMenuData, isError }] =
    usePublishMenuMutation();

  const hasDeleteMenuPermission = useHasPermission('DELETE_MENU');
  const hasEditMenuPermission = useHasPermission('EDIT_MENU');
  const hasPublishMenuPermission = useHasPermission(
    'MENU_IMPORT_FOR_LOCATIONS',
  );
  const displayInfoColumns = useMenuManagementDisplayInfoColumns();

  useMenusToast(publishMenuData?.status, data, isError);

  const handlePublishMenu = useCallback(
    async (data: MenuResult) => {
      setData(data);
      try {
        await publishMenu({ storeIds: data?.storeIds }).unwrap();
      } catch (_e) {}
    },
    [publishMenu],
  );

  usePollPublishMenuStatus(publishMenuData, publishMenu, data?.storeIds);

  const getActions = useCallback(
    params => {
      const actions = [];

      if (hasEditMenuPermission) {
        actions.push(
          <GridActionsCellItem
            icon={<EditOutlined />}
            label={t('MenusDataGrid.column.actions.edit.label')}
            onClick={() => navigate(`../${params.id}`)}
            showInMenu
          />,
        );
      }

      if (hasPublishMenuPermission) {
        actions.push(
          <GridActionsCellItem
            icon={<UploadOutlined />}
            label={t('MenusDataGrid.column.actions.publish.label')}
            onClick={() => handlePublishMenu(params.row)}
            showInMenu
          />,
        );
      }

      if (hasDeleteMenuPermission) {
        actions.push(
          <GridActionsCellItem
            icon={<DeleteOutlined color="error" />}
            label={t('MenusDataGrid.column.actions.delete.label')}
            onClick={() => {
              deleteConfirmationDrawer.setDrawerData(params.row);
              deleteConfirmationDrawer.setOpen(true);
            }}
            showInMenu
          />,
        );
      }

      return actions;
    },
    [
      deleteConfirmationDrawer,
      handlePublishMenu,
      hasDeleteMenuPermission,
      hasEditMenuPermission,
      hasPublishMenuPermission,
      navigate,
      t,
    ],
  );

  const columns: BulkEditGridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: t('MenusDataGrid.column.title.label'),
        minWidth: 400,
        hideable: false,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'originalTitle',
        headerName: t('MenusDataGrid.column.externalTitle.label'),
        minWidth: 150,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'storeInfos',
        headerName: t('MenusDataGrid.column.locations.label'),
        minWidth: 450,
        display: 'flex',
        renderCell: params => <MenusStores stores={params.value} />,
      },
      {
        field: 'updatedDate',
        headerName: t('MenusDataGrid.column.lastPublish.label'),
        minWidth: 200,
        display: 'flex',
        renderCell: params => (
          <Typography>
            {DateTime.fromISO(params.value, { zone: 'utc' })
              .setZone('local')
              .toLocaleString(DateTime.DATETIME_MED)}
          </Typography>
        ),
      },
      {
        field: 'catalogId',
        headerName: t('MenusDataGrid.column.id.label'),
        minWidth: 250,
        sortable: false,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'externalId',
        headerName: t('MenusDataGrid.column.externalId.label'),
        minWidth: 300,
        sortable: false,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'startDate',
        headerName: t('MenusDataGrid.column.startDate.label'),
        minWidth: 200,
        display: 'flex',
        renderCell: params => (
          <Typography>
            {DateTime.fromISO(params.value).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </Typography>
        ),
      },
      {
        field: 'endDate',
        headerName: t('MenusDataGrid.column.endDate.label'),
        minWidth: 200,
        display: 'flex',
        renderCell: params => (
          <Typography>
            {DateTime.fromISO(params.value).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </Typography>
        ),
      },
      {
        field: 'extendedAttributes',
        headerName: t('MenusDataGrid.column.extendedAttributes.label'),
        minWidth: 300,
        sortable: false,
        type: 'custom',
        customType: 'keyValue',
        display: 'flex',
        renderCell: params => (
          <Typography title={JSON.stringify(params.value)}>
            {Object.keys(params.value).length > 0 &&
              JSON.stringify(params.value)}
          </Typography>
        ),
      },
      {
        field: 'displayRank',
        headerName: t('MenusDataGrid.column.displayRank.label'),
        minWidth: 150,
        type: 'number',
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'status',
        headerName: t('MenusDataGrid.column.status.label'),
        minWidth: 150,
        type: 'singleSelect',
        valueOptions: [
          { id: 'ACTIVE', label: 'Active' },
          { id: 'INACTIVE', label: 'Inactive' },
        ],
        display: 'flex',
        renderCell: params => (
          <IncActiveInactiveStatusChip value={params.value} />
        ),
      },
      ...displayInfoColumns,
      ...(!hasEditMenuPermission &&
      !hasPublishMenuPermission &&
      !hasDeleteMenuPermission
        ? []
        : [
            {
              field: t('SearchDataGrid.column.actions'),
              type: 'actions',
              hideable: false,
              getActions,
              resizable: false,
            } as GridColDef,
          ]),
    ],
    [
      displayInfoColumns,
      getActions,
      hasDeleteMenuPermission,
      hasEditMenuPermission,
      hasPublishMenuPermission,
      t,
    ],
  );

  return isNative
    ? columns.filter(
        column =>
          column.field !== 'originalTitle' && column.field !== 'externalId',
      )
    : columns;
};

export const useMenusToolbar = (
  isNative: boolean,
  bulkDeleteConfirmationDrawer: UtilityDrawerHookReturnType<MenuResult>,
): ToolbarProps => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedRowIds } = useBulkEditDataGridContext();

  const [catalogsBulkUpdate] = useCatalogsBulkUpdateMutation();

  const hasEditMenuPermission = useHasPermission('EDIT_MENU');
  const hasDeleteMenuPermission = useHasPermission('DELETE_MENU');

  const snackbarForBulkEdit = field => {
    switch (field) {
      case 'status':
        return t('MenusDataGrid.bulkEdit.status.successMessage');
      case 'displayRank':
        return t('MenusDataGrid.bulkEdit.displayRank.successMessage');
      case 'extendedAttributes':
        return t('MenusDataGrid.bulkEdit.extendedAttributes.successMessage');
      case 'startDate':
        return t('MenusDataGrid.bulkEdit.startDate.successMessage');
      case 'endDate':
        return t('MenusDataGrid.bulkEdit.endDate.successMessage');
      case 'displayTitle':
        return t('MenusDataGrid.bulkEdit.displayTitle.successMessage');
      case 'shortDescription':
        return t('MenusDataGrid.bulkEdit.shortDescription.successMessage');
      case 'longDescription':
        return t('MenusDataGrid.bulkEdit.longDescription.successMessage');
      case 'smallImage':
        return t('MenusDataGrid.bulkEdit.smallImage.successMessage');
      case 'mediumImage':
        return t('MenusDataGrid.bulkEdit.mediumImage.successMessage');
    }
  };

  const modifieldValue = (field, value) => {
    switch (field) {
      case 'extendedAttributes':
        return additionalPropertiesToObject(value);
      case 'startDate':
        return DateTime.fromISO(value)
          .set({ hour: 0, minute: 0, second: 0 })
          .toISO({ includeOffset: false, suppressMilliseconds: true });
      case 'endDate':
        return DateTime.fromISO(value)
          .set({ hour: 23, minute: 59, second: 59 })
          .toISO({ includeOffset: false, suppressMilliseconds: true });
      default:
        return toBulkUpdateValue(field, value);
    }
  };

  const {
    status,
    startDate,
    endDate,
    extendedAttributes,
    displayRank,
    displayInfo,
  } = useMenuManagementBulkEditFields();

  const bulkEditFields = useMemo(() => {
    if (hasEditMenuPermission) {
      return {
        status,
        extendedAttributes,
        displayRank,
        // start/end dates can only be edited on native menus
        ...(isNative ? { startDate, endDate } : {}),
        ...displayInfo,
      };
    }
    return {};
  }, [
    displayInfo,
    displayRank,
    endDate,
    extendedAttributes,
    hasEditMenuPermission,
    isNative,
    startDate,
    status,
  ]);

  const bulkEditAdditionalMenus = useMemo(
    () =>
      hasDeleteMenuPermission
        ? [
            <MenuItem
              key="delete"
              onClick={() => bulkDeleteConfirmationDrawer.setOpen(true)}
            >
              <ListItemIcon>
                <DeleteOutlined fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>
                {t('MenusDataGrid.additionalMenus.delete.label')}
              </ListItemText>
            </MenuItem>,
          ]
        : undefined,
    [bulkDeleteConfirmationDrawer, hasDeleteMenuPermission, t],
  );

  const handleBulkUpdate = async data => {
    const field = Object.keys(data)[0];
    const value = data[field];

    try {
      await catalogsBulkUpdate({
        ids: selectedRowIds,
        field: toBulkUpdateField(field),
        value: modifieldValue(field, value),
      }).unwrap();
      enqueueSnackbar(snackbarForBulkEdit(field), { severity: 'success' });
    } catch (_e) {}
  };

  const toolbar: ToolbarProps = {
    columns: { enabled: true },
    bulkEdit: {
      enabled: hasEditMenuPermission || hasDeleteMenuPermission,
      fields: bulkEditFields,
      additionalMenuItems: bulkEditAdditionalMenus,
      onValid: data => handleBulkUpdate(data),
    },
  };

  return toolbar;
};

export const useMenusInitialState = (): GridInitialStatePremium => {
  const { t } = useTranslation();

  return {
    pinnedColumns: { right: [t('SearchDataGrid.column.actions')] },
    columns: {
      columnVisibilityModel: {
        catalogId: false,
        externalId: false,
        startDate: false,
        endDate: false,
        extendedAttributes: false,
        displayRank: false,
        ...displayInfoColumnVisibilityModel,
      },
    },
  };
};
